import { AppThunk } from "../../store/AppThunkType";
import {
    loadMyPermitsSuccessful,
    loadMyPermitsStarted,
    loadMyPermitsFailed,
    loadClientRolesStarted,
    loadClientRolesSuccessful,
    loadReferenceProceduresSuccessful,
    permitPendingSyncUpdatedSuccessful,
} from "./actions";
import { logOut } from "../AppControlContainer/actions";
import Database from "../../Database";
import { READY_TO_ISSUE, VIEW_ISSUED_PERMITS, VIEW_READY_TO_ISSUE_PERMITS } from "../../constants";
import database_Local from "../../Database_Local";
import { AppState } from "../../store";
import { setCurrentProfile, setCurrentUser } from "../LoginContainer/actions";
import { UserProfile } from "../../models/UserProfile";
import { getSyncGatewayDetails } from "../LoginContainer/thunks";
import database from "../../Database";
import { Permit } from "../../models/Permit";

export function loadMyPermits(): AppThunk {
    return function (dispatch, getState: () => AppState) {
        dispatch(loadMyPermitsStarted());

        Database.queryPermits()
            .then((result) => {
                const permitItems = result;
                const currentUser = getState().user.currentUser;
                if (!currentUser) {
                    dispatch(loadMyPermitsSuccessful([]));
                    return;
                }
                const clientRoles = getState().myPermits.clientRoles;

                const getPermissionKeysForPermit = (permit: Permit): string[] => {
                    const matchingAssignments = currentUser.assignments.filter(
                        (a) =>
                            a.permitRegister?.id === permit.permitRegister?.id &&
                            a.permitType?.id === permit.permitType?.id
                    );
                    const permissionKeys: string[] = [];
                    for (const assignment of matchingAssignments) {
                        const role = clientRoles.find((r) => r.id === assignment.roleId);
                        if (role) {
                            permissionKeys.push(...role.permissionKeys);
                        }
                    }
                    return permissionKeys;
                };

                const availablePermitItems = permitItems.filter((item) => {
                    const permissionKeys = getPermissionKeysForPermit(item);
                    if (permissionKeys.length === 0) return false;

                    // Users with "PERMIT_ISSUER access" have access to all permits, no matter the status
                    //if (permissionKeys.includes("IssuePermit")) return true;

                    // Users with "View only/Permit receivers" don't have access to "Ready to issue" permits
                    if (
                        !!item.permitAssignments?.find((a) => a.userId === currentUser.id) ||
                        (permissionKeys.includes(VIEW_READY_TO_ISSUE_PERMITS) && item.status === READY_TO_ISSUE) ||
                        (permissionKeys.includes(VIEW_ISSUED_PERMITS) && item.status !== READY_TO_ISSUE)
                    )
                        return true;

                    return false;
                });
                dispatch(loadMyPermitsSuccessful(availablePermitItems));

                availablePermitItems
                    .filter((permit) => permitRequiresSync(permit))
                    .forEach((permit) => {
                        dispatch(setPermitPendingSyncFlag(permit));
                    });
            })
            .catch((error) => {
                dispatch(loadMyPermitsFailed(error));
                database_Local.deleteAll();
                dispatch(logOut());
            });
    };
}

export function loadClientRoles(clientId: string): AppThunk {
    return function (dispatch) {
        dispatch(loadClientRolesStarted());

        Database.queryClientRoles(clientId).then((result) => {
            dispatch(loadClientRolesSuccessful(result));
        });
    };
}

export function loadReferenceProcedures(clientId: string): AppThunk {
    return function (dispatch) {
        Database.queryReferenceProcedures(clientId).then((result) => {
            dispatch(loadReferenceProceduresSuccessful(result));
        });
    };
}

export function loadSyncedUser(userId: string): AppThunk {
    return function (dispatch, getState: () => AppState) {
        Database.queryUser(userId).then((result) => {
            const currentUser = getState().user.currentUser;
            const currentProfile = getState().user.currentProfile;

            const isUserInCurrentProfile = currentProfile?.clientUsers.find((u) => u.id === result.id);
            if (isUserInCurrentProfile) {
                const newProfile =
                    currentProfile &&
                    ({
                        ...currentProfile,
                        clientUsers: currentProfile.clientUsers.map((u) => (u.id === result.id ? result : u)),
                    } as UserProfile);
                database_Local.save("userProfile", newProfile);
                newProfile && dispatch(setCurrentProfile(newProfile));
            }

            const userIsCurrentUser = currentUser?.id === result.id;
            if (userIsCurrentUser) {
                database_Local.save("currentUser", result);
                dispatch(setCurrentUser(result));
            }
        });
    };
}

export function tryToConnect(userProfile: UserProfile): AppThunk {
    return function () {
        const { syncGatewayAddress, syncGatewayDatabase, isSsl } = getSyncGatewayDetails();

        database
            .openDatabase(
                syncGatewayAddress,
                isSsl,
                syncGatewayDatabase,
                userProfile.email,
                userProfile.syncGatewaySession
            )
            .then(() => {
                console.log("successfully connected");
            })
            .catch((error: any) => {
                console.log(error);
            });
    };
}

export function saveToken(token: string): AppThunk {
    return function (dispatch, getState: () => AppState) {
        const oldToken = database_Local.load<string>("pushNotificationToken");
        const userProfile = getState().user.currentProfile;

        if (!userProfile || !token) {
            return;
        }

        userProfile.clientUsers.forEach((u) => {
            const pushNotificationTokens = u.pushNotificationTokens
                ? [token, ...u.pushNotificationTokens.filter((t) => t !== (oldToken ?? ""))]
                : [token];

            database.saveUser({
                ...u,
                pushNotificationTokens: pushNotificationTokens,
            });
        });

        database_Local.save("pushNotificationToken", token);
    };
}

export function setPermitPendingSyncFlag(permit: Permit): AppThunk {
    return function (dispatch, getState) {
        const updatedPermit = { ...permit, pendingSync: true };

        database.savePermit(updatedPermit, getState().permitPage.newFiles).then((result) => {
            dispatch(permitPendingSyncUpdatedSuccessful(permit.id));
        });
    };
}

function permitRequiresSync(permit: Permit): boolean {
    var gangMembersRequireSync =
        permit.permitGangMembers?.some((gm) => (gm.pendingSync || gm.pendingDelete) && !gm.deleted) ?? false;
    var gangMembersSignaturesRequireSync = permit.permitGangMembers?.some(
        (gm) =>
            !gm.deleted &&
            (gm.signatures?.some((sig) => (sig.pendingSync || sig.pendingDelete) && !sig.deleted) ?? false)
    );

    return gangMembersRequireSync || gangMembersSignaturesRequireSync;
}
